import { useQuery } from "@tanstack/react-query";
import { Card, Spinner } from "@nutrayan/ui";
import { apiClient } from "@/lib/api-client";

// Contract: GET /admin/api/v1/analytics/funnel and /analytics/products → report rows.

interface FunnelRow {
  date?: string;
  sessions?: number;
  atcEvents?: number;
  checkoutStarts?: number;
  purchases?: number;
}

interface ProductRow {
  date?: string;
  variantId?: number;
  atcEvents?: number;
  unitsAddedToCart?: number;
}

export default function AnalyticsPage() {
  const funnel = useQuery({
    queryKey: ["admin", "analytics", "funnel"],
    queryFn: () => apiClient.request<{ rows?: FunnelRow[] }>("/admin/api/v1/analytics/funnel"),
  });
  const products = useQuery({
    queryKey: ["admin", "analytics", "products"],
    queryFn: () => apiClient.request<{ rows?: ProductRow[] }>("/admin/api/v1/analytics/products"),
  });

  return (
    <div className="max-w-4xl space-y-8">
      <header>
        <h1 className="font-display text-display-xl text-charcoal">Analytics</h1>
        <p className="text-body text-ink">Conversion funnel and product performance.</p>
      </header>

      <section>
        <h2 className="font-display text-display-md text-charcoal">
          Funnel — sessions → ATC → checkout → purchase
        </h2>
        {funnel.isPending ? (
          <Spinner className="mt-3" />
        ) : funnel.isError ? (
          <Card className="mt-3">
            <p className="text-body text-terracotta">Couldn&apos;t load the funnel report.</p>
          </Card>
        ) : (funnel.data?.rows ?? []).length === 0 ? (
          <Card className="mt-3">
            <p className="text-body text-ink">No funnel data in this range yet.</p>
          </Card>
        ) : (
          <Card className="mt-3 p-0">
            <table className="text-body-sm w-full">
              <thead className="border-charcoal/10 text-caption text-muted border-b text-left">
                <tr>
                  <th className="px-4 py-2">Date</th>
                  <th className="px-4 py-2 text-right">Sessions</th>
                  <th className="px-4 py-2 text-right">ATC</th>
                  <th className="px-4 py-2 text-right">Checkouts</th>
                  <th className="px-4 py-2 text-right">Purchases</th>
                </tr>
              </thead>
              <tbody>
                {(funnel.data?.rows ?? []).map((r, i) => (
                  <tr key={r.date ?? i} className="border-charcoal/5 border-b">
                    <td className="text-ink px-4 py-2">{r.date ?? "—"}</td>
                    <td className="text-ink px-4 py-2 text-right">{r.sessions ?? 0}</td>
                    <td className="text-ink px-4 py-2 text-right">{r.atcEvents ?? 0}</td>
                    <td className="text-ink px-4 py-2 text-right">{r.checkoutStarts ?? 0}</td>
                    <td className="text-charcoal px-4 py-2 text-right font-medium">
                      {r.purchases ?? 0}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
      </section>

      <section>
        <h2 className="font-display text-display-md text-charcoal">
          Product performance — cart adds per variant
        </h2>
        {products.isPending ? (
          <Spinner className="mt-3" />
        ) : products.isError ? (
          <Card className="mt-3">
            <p className="text-body text-terracotta">Couldn&apos;t load product performance.</p>
          </Card>
        ) : (products.data?.rows ?? []).length === 0 ? (
          <Card className="mt-3">
            <p className="text-body text-ink">No product events in this range yet.</p>
          </Card>
        ) : (
          <Card className="mt-3 p-0">
            <table className="text-body-sm w-full">
              <thead className="border-charcoal/10 text-caption text-muted border-b text-left">
                <tr>
                  <th className="px-4 py-2">Date</th>
                  <th className="px-4 py-2">Variant</th>
                  <th className="px-4 py-2 text-right">ATC events</th>
                  <th className="px-4 py-2 text-right">Units</th>
                </tr>
              </thead>
              <tbody>
                {(products.data?.rows ?? []).map((r, i) => (
                  <tr key={`${r.date}-${r.variantId}-${i}`} className="border-charcoal/5 border-b">
                    <td className="text-ink px-4 py-2">{r.date ?? "—"}</td>
                    <td className="text-ink px-4 py-2 font-mono">#{r.variantId ?? "—"}</td>
                    <td className="text-ink px-4 py-2 text-right">{r.atcEvents ?? 0}</td>
                    <td className="text-ink px-4 py-2 text-right">{r.unitsAddedToCart ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
      </section>
    </div>
  );
}
